import React, { useState } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { register as registerApi } from '../../services/auth';
import SocialAuthButtons from '../../components/Auth/SocialAuthButtons';
import AuthShell from '../../components/Auth/AuthShell';
import { useAuth } from '../../contexts/AuthContext';
import { encrypt } from '../../utils/crypto';
import { LegalInline } from '../../components/Legal/LegalPage';
import { rememberReturnTo, returnStateFrom } from '../../utils/authRedirect';

/** 0532…, 90532…, +90 532… → 5321234567 */
const normalizePhone = (raw) => {
  let d = String(raw || '').replace(/\D/g, '');
  if (d.length === 12 && d.startsWith('90')) d = d.slice(2);
  if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
  return d.slice(0, 10);
};
const formatPhone = (d = '') =>
  [d.slice(0, 3), d.slice(3, 6), d.slice(6, 8), d.slice(8, 10)].filter(Boolean).join(' ');

const StepProgress = ({ steps, active }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${steps.length}, 1fr)`, gap: 1, mb: 3.5 }}>
    {steps.map((label, i) => {
      const done = i < active;
      const current = i === active;
      return (
        <Box key={label}>
          <Box
            sx={{
              height: 4,
              borderRadius: 4,
              bgcolor: done || current ? 'primary.main' : 'divider',
              opacity: current ? 1 : done ? 0.55 : 1,
              transition: 'background-color .2s',
            }}
          />
          <Typography
            noWrap
            sx={{ mt: 1, fontSize: 12.5, fontWeight: current ? 700 : 550, color: current ? 'text.primary' : 'text.secondary' }}
          >
            {label}
          </Typography>
        </Box>
      );
    })}
  </Box>
);

const RegisterPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  // Dönüş adresi (ör. Mülk Takibi tanıtımından gelen kullanıcı → /mulk-takibi/panel); etkinleştirmeye taşınır
  const returnState = returnStateFrom(location);

  const steps = [t('auth.register.step1'), t('auth.register.additionalInfo'), t('auth.register.step3')];

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    lastName: '',
    email: '',
    phone: '',
    phoneCode: '90',
    bornYear: null,
    gender: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false,
    // Ticari elektronik ileti onayı — isteğe bağlı, varsayılan kapalı (backend: UserRegister.MarketingConsent)
    marketingConsent: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [termsDialog, setTermsDialog] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const set = (key) => (e) => setFormData((f) => ({ ...f, [key]: e.target.value }));

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateStep1 = () => {
    if (!formData.name || !formData.lastName || !formData.email || !formData.phone) {
      toast.error(t('auth.register.fillAllFields'));
      return false;
    }
    if (!validateEmail(formData.email)) {
      toast.error(t('auth.register.invalidEmail'));
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.bornYear || !formData.gender) {
      toast.error(t('auth.register.fillAllFields'));
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!formData.password || !formData.confirmPassword) {
      toast.error(t('auth.register.fillAllFields'));
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error(t('auth.register.passwordMismatch'));
      return false;
    }
    if (!formData.acceptTerms) {
      toast.error(t('auth.register.acceptTerms'));
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const response = await registerApi({
        ...formData,
        bornYear: formData.bornYear.toISOString(),
        phone: normalizePhone(formData.phone),
      });

      if (response.statusCode === 200) {
        const token = response.token || response.data?.token;
        if (token) {
          localStorage.setItem('authToken', encrypt(token));
          setUser({
            id: response.data?.id,
            isActivated: false,
          });
        }
        toast.success(t('auth.register.success'));
        rememberReturnTo(returnState?.from);
        navigate('/activation', { state: returnState });
      } else {
        if (response.message) {
          toast.error(t(`auth.register.errors.${response.message}`, response.message));
        }

        if (response.errors && Array.isArray(response.errors) && response.errors.length > 0) {
          response.errors.forEach((error) => toast.error(error));
        } else if (response.statusCode === 400 && response.message === 'Identity verification error') {
          toast.error(
            t('auth.register.errors.identityVerification', 'Kimlik doğrulama hatası. Lütfen bilgilerinizi kontrol ediniz.')
          );
        }
      }
    } catch (error) {
      console.error('Kayıt hatası:', error);
      toast.error(t('auth.register.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = (e) => {
    e?.preventDefault();
    const validators = [validateStep1, validateStep2, validateStep3];
    if (!validators[activeStep]()) return;
    if (activeStep === steps.length - 1) {
      handleSubmit();
      return;
    }
    setActiveStep((prev) => prev + 1);
  };

  const passwordAdornment = (
    <InputAdornment position="end">
      <IconButton
        aria-label={showPassword ? t('auth.login.password.hide') : t('auth.login.password.show')}
        onClick={() => setShowPassword((v) => !v)}
        edge="end"
      >
        {showPassword ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  const stepContent = [
    <Grid container spacing={2} key="s0">
      <Grid item xs={12} sm={6}>
        <TextField fullWidth required label={t('auth.register.name')} value={formData.name} onChange={set('name')} autoComplete="given-name" autoFocus />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth required label={t('auth.register.lastName')} value={formData.lastName} onChange={set('lastName')} autoComplete="family-name" />
      </Grid>
      <Grid item xs={12}>
        <TextField fullWidth required type="email" label={t('auth.register.email')} value={formData.email} onChange={set('email')} autoComplete="email" />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          required
          label={t('auth.register.phone')}
          value={formatPhone(formData.phone)}
          onChange={(e) => setFormData((f) => ({ ...f, phone: normalizePhone(e.target.value) }))}
          placeholder="5xx xxx xx xx"
          helperText={t('auth.register.phoneHint')}
          inputProps={{ inputMode: 'tel', autoComplete: 'tel-national' }}
          InputProps={{ startAdornment: <InputAdornment position="start">+90</InputAdornment> }}
        />
      </Grid>
    </Grid>,

    <Grid container spacing={2} key="s1">
      <Grid item xs={12}>
        <DatePicker
          label={t('auth.register.bornYear')}
          value={formData.bornYear}
          onChange={(date) => setFormData((f) => ({ ...f, bornYear: date }))}
          disableFuture
          openTo="year"
          views={['year', 'month', 'day']}
          slotProps={{ textField: { fullWidth: true, required: true } }}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControl fullWidth required>
          <InputLabel id="register-gender-label">{t('auth.register.gender')}</InputLabel>
          <Select labelId="register-gender-label" value={formData.gender} onChange={set('gender')} label={t('auth.register.gender')}>
            <MenuItem value="201">{t('auth.register.male')}</MenuItem>
            <MenuItem value="104">{t('auth.register.female')}</MenuItem>
          </Select>
        </FormControl>
      </Grid>
    </Grid>,

    <Grid container spacing={2} key="s2">
      <Grid item xs={12}>
        <TextField
          fullWidth
          required
          label={t('auth.register.password')}
          type={showPassword ? 'text' : 'password'}
          value={formData.password}
          onChange={set('password')}
          autoComplete="new-password"
          helperText={t('auth.register.passwordHint')}
          InputProps={{ endAdornment: passwordAdornment }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          required
          label={t('auth.register.confirmPassword')}
          type={showPassword ? 'text' : 'password'}
          value={formData.confirmPassword}
          onChange={set('confirmPassword')}
          autoComplete="new-password"
          error={!!formData.confirmPassword && formData.confirmPassword !== formData.password}
        />
      </Grid>
      <Grid item xs={12}>
        {/* Zorunlu: Kullanım Koşulları kabulü */}
        <FormControlLabel
          sx={{ alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0.25 } }}
          control={
            <Checkbox
              required
              checked={formData.acceptTerms}
              onChange={(e) => setFormData((f) => ({ ...f, acceptTerms: e.target.checked }))}
            />
          }
          label={
            <Typography sx={{ fontSize: 13.5, color: 'text.secondary', lineHeight: 1.5 }}>
              <LegalInline text={t('auth.register.consentTerms')} linkTarget="_blank" />
            </Typography>
          }
        />
        {/* İsteğe bağlı: ticari elektronik ileti onayı (önceden işaretli DEĞİL) */}
        <FormControlLabel
          sx={{ mt: 1, alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0.25 } }}
          control={
            <Checkbox
              checked={formData.marketingConsent}
              onChange={(e) => setFormData((f) => ({ ...f, marketingConsent: e.target.checked }))}
            />
          }
          label={
            <Typography sx={{ fontSize: 13.5, color: 'text.secondary', lineHeight: 1.5 }}>
              <LegalInline text={t('auth.register.consentMarketing')} linkTarget="_blank" />
            </Typography>
          }
        />
        {/* Aydınlatma bilgilendirmedir, onay kutusu değildir */}
        <Typography sx={{ mt: 1.5, fontSize: 12.5, color: 'text.secondary', lineHeight: 1.5 }}>
          <LegalInline text={t('auth.register.consentKvkk')} linkTarget="_blank" />
        </Typography>
      </Grid>
    </Grid>,
  ];

  const isLast = activeStep === steps.length - 1;

  return (
    <AuthShell maxFormWidth={460}>
      <Typography sx={{ fontSize: 13, fontWeight: 650, color: 'primary.main', letterSpacing: '0.02em' }}>
        {t('auth.register.stepIndicator', { current: activeStep + 1, total: steps.length })}
      </Typography>
      <Typography component="h1" sx={{ mt: 0.75, fontSize: { xs: '1.6rem', sm: '1.85rem' }, fontWeight: 750, letterSpacing: '-0.025em', color: 'text.primary' }}>
        {t('auth.register.heading')}
      </Typography>
      <Typography sx={{ mt: 1, mb: 3, color: 'text.secondary', fontSize: '0.95rem' }}>{t('auth.register.lead')}</Typography>

      {activeStep === 0 ? (
        <Box sx={{ mb: 1 }}>
          <SocialAuthButtons mode="register" layout="row" divider="bottom" dividerText={t('auth.register.orEmail')} returnTo={returnState?.from} />
        </Box>
      ) : null}

      <StepProgress steps={steps} active={activeStep} />

      <Box component="form" onSubmit={handleNext} noValidate>
        {stepContent[activeStep]}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, mt: 3.5 }}>
          {activeStep > 0 ? (
            <Button onClick={() => setActiveStep((p) => p - 1)} startIcon={<ArrowBackIcon />} sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}>
              {t('common.back')}
            </Button>
          ) : (
            <Box />
          )}
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={isLoading}
            endIcon={isLoading || isLast ? null : <ArrowForwardIcon />}
            sx={{ px: 3.5, py: 1.3, borderRadius: '10px', fontWeight: 700, textTransform: 'none', minWidth: 160 }}
          >
            {isLoading ? <CircularProgress size={22} color="inherit" /> : isLast ? t('auth.register.submit') : t('common.next')}
          </Button>
        </Box>
      </Box>

      <Typography sx={{ mt: 3.5, pt: 2.5, borderTop: 1, borderColor: 'divider', textAlign: 'center', fontSize: 14, color: 'text.secondary' }}>
        {t('auth.register.haveAccount')}{' '}
        <Link component={RouterLink} to="/login" state={returnState} underline="hover" sx={{ fontWeight: 700 }}>
          {t('auth.register.loginButton')}
        </Link>
      </Typography>

      <Dialog open={termsDialog} onClose={() => setTermsDialog(false)}>
        <DialogTitle>{t('auth.register.termsTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('auth.register.termsContent')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTermsDialog(false)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    </AuthShell>
  );
};

export default RegisterPage;
